import React, { useCallback, useEffect, useRef, useState } from 'react';
import _get from 'lodash/get';
import _trim from 'lodash/trim';
import _isEmpty from 'lodash/isEmpty';
import _isArray from 'lodash/isArray';
import _isEqual from 'lodash/isEqual';
import _cloneDeep from 'lodash/cloneDeep';
import styles from './index.module.scss';

import { If } from 'utils';
import { useDebounce, useSeriesFetch, useToggleMap } from 'utils/hooks';
import { PageErrorUi } from 'components';
import {
  Table,
  TableHead,
  TableRows,
  TableLoader,
  TablePaginate,
  TableSearch,
  TableZeroState
} from 'components/Tables';
import Rows from './Rows';
import {
  createInitialCheckedProductsMap,
  createInitialExpandedProductsMap,
  createProductsData
} from '../../methods';
import { PRODUCTS_MODAL_TABLE_HEADERS as HEADERS } from '../../stubs';
import { ReactComponent as ProductIcon } from 'assets/ss-icons/zero-state-icons/product.svg';

const ProductsSearchTable = ({
  windowId = '',
  data = [],
  pageSize = 20,
  onDataChange = () => {}
}) => {
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [checked, setChecked] = useState({});
  const { active, onToggle, setActive } = useToggleMap({});
  const [searchValue, setSearchValue] = useState('');
  const searchQuery = useDebounce(_trim(searchValue));
  const dataRef = useRef([]);

  const { data: products, isError, isLoading, fetchData } = useSeriesFetch(
    [
      {
        dataKey: 'products',
        apiKey: 'products.getAll',
        method: 'POST',
        payload: { page, search: searchQuery, page_size: pageSize }
      },
      {
        dataKey: 'productVariants',
        apiKey: 'productVaraints.post',
        method: 'POST',
        payload: aggregateData => {
          const products = _get(aggregateData, 'products.results', []);
          const productIds = products.map(product => {
            return _get(product, '_source.ObjectId');
          });
          return {
            product_ids: [...new Set(productIds)],
            out_of_stock_variants: true,
            is_deleted: false
          };
        },
        onTransform: data => (_isArray(data) ? data : [])
      },
      {
        dataKey: 'blockedVariants',
        apiKey: 'configure.serviceWindow.exceptions.productSkus.validate',
        method: 'POST',
        payload: aggregateData => {
          const productVariants = _get(aggregateData, 'productVariants', []);
          const productVaraintIds = productVariants.map(productVariant => {
            return _get(productVariant, 'objectID', '');
          });
          const payload = { product_variants: productVaraintIds };
          if (windowId) {
            payload.exception_id = windowId;
          }
          return payload;
        }
      }
    ],
    {
      initialData: [],
      loadOnMount: false,
      onTransform: createProductsData,
      onSuccess: (_, rawData) => {
        const page = _get(rawData, 'products.page', 1);
        const totalRecords = _get(rawData, 'products.total_records', 0);
        const totalPages = Math.ceil(totalRecords / pageSize);
        setPage(page);
        setTotalPages(totalPages);
      }
    }
  );

  useEffect(() => {
    fetchData({ products: { page: 1, search: searchQuery } });
  }, [searchQuery, fetchData]);

  useEffect(() => {
    if (!_isEmpty(data) && !_isEqual(data, dataRef.current)) {
      dataRef.current = _cloneDeep(data);
      setChecked(() => createInitialCheckedProductsMap(data));
      setActive(() => createInitialExpandedProductsMap(data));
    }
  }, [data, setActive]);

  useEffect(() => {
    const products = Object.values(checked).map(dataset => {
      const product = _get(dataset, 'data', {});
      const variantsMap = _get(dataset, 'variants', {});
      product.productVariants = Object.values(variantsMap);
      return product;
    });
    onDataChange(products);
  }, [checked, onDataChange]);

  const onPageChange = useCallback(
    page => {
      setPage(page);
      fetchData({ products: { page } });
    },
    [fetchData]
  );

  return (
    <>
      <div className={styles.searchContainer}>
        <TableSearch
          value={searchValue}
          onChange={query => setSearchValue(query)}
          onCancel={() => setSearchValue('')}
          placeholder="Search a product"
        />
      </div>
      <Table className={styles.table}>
        <div className={styles.header}>
          <TableHead headings={HEADERS} className={styles.head} />
        </div>
        <PageErrorUi isError={isError} onReload={fetchData}>
          <TableRows className={styles.body}>
            <TableLoader
              isLoading={isLoading}
              headers={HEADERS}
              rowsCount={4}
              rowClassName={styles.loaderRow}
            >
              <If
                test={!_isEmpty(products)}
                otherwise={
                  <TableZeroState
                    searchQuery={searchQuery}
                    icon={<ProductIcon />}
                  >
                    There is no product.
                  </TableZeroState>
                }
              >
                <Rows
                  products={products}
                  searchQuery={searchQuery}
                  checked={checked}
                  expanded={active}
                  onToggle={onToggle}
                  onCheck={setChecked}
                />
              </If>
            </TableLoader>
            <TablePaginate
              page={page}
              totalPages={totalPages}
              setPage={onPageChange}
              isDisabled={isLoading}
            />
          </TableRows>
        </PageErrorUi>
      </Table>
    </>
  );
};

export default ProductsSearchTable;
